import { useMemo, useState } from "react";
import { ChevronRight, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { SPOR, SPOR_ALLE, SPOR_GOAL, MILEPAEL, fmtDue, statusEn, statusTone, hentMedlemmer, patchEier, patchNeste } from "./blokkData";
import AssignmentTabell from "./AssignmentTabell";

const StatusChip = ({ r }) => (
  <span className={`dok-status-chip t-${statusTone(r.stage)}`}>{statusEn(r)}</span>
);

const PRI_NESTE = { P1: "P2", P2: "P3", P3: null };

const SIKRET = new Set(["signed", "confirmed", "contracted", "terms", "verbal"]);
const PASSIV = new Set(["target", "lead", "declined", ""]);
const erAktiv = (r) => !PASSIV.has(r.stage || "") && !SIKRET.has(r.stage);

const sporStatus = (rader) => {
  if (rader.some((r) => SIKRET.has(r.stage))) return ["secured", "Sikret"];
  if (rader.some((r) => erAktiv(r))) return ["progress", "I dialog"];
  return ["not", "Ikke sikret"];
};

const Due = ({ frist }) => {
  const d = fmtDue(frist);
  if (!d) return null;
  return <span className={`dok-due ${d.tone}`}>{d.tone ? d.label : `Due ${d.label}`}</span>;
};

function MedlemsMeny({ onVelg, onLukk, medlemmer }) {
  return (
    <span className="dok-eier-meny" onClick={(e) => e.stopPropagation()} data-testid="dok-eier-meny">
      {medlemmer.map((m) => (
        <button key={m.id} type="button" onClick={() => onVelg(m)}>
          <i style={{ background: m.color }}>{(m.name || "?")[0]}</i>{m.name}
          {m.status === "invitert" && <span className="inv">invitert</span>}
        </button>
      ))}
      <button type="button" onClick={() => onVelg(null)} className="ingen">Ingen eier</button>
      <button type="button" className="lukk" onClick={onLukk}><X size={10} /></button>
    </span>
  );
}

export function EierChip({ r, editable, onSatt }) {
  const [meny, setMeny] = useState(false);
  const [medl, setMedl] = useState([]);
  const aapne = async (e) => {
    e.stopPropagation();
    if (!editable) return;
    try { setMedl(await hentMedlemmer()); setMeny(true); } catch { toast.error("Kunne ikke hente teamet"); }
  };
  const velg = async (m) => {
    setMeny(false);
    try {
      await patchEier(r, m ? m.id : null);
      onSatt();
    } catch { toast.error("Kunne ikke sette eier"); }
  };
  return (
    <span className="dok-eier-wrap">
      <button type="button" tabIndex={editable ? 0 : -1}
        className={`dok-eier-chip ${r.ansvarlig ? "" : "uten"} ${editable ? "kan" : ""}`}
        onClick={aapne} data-testid={`dok-rad-eier-${r.id}`}>
        {r.ansvarlig
          ? <><i style={{ background: r.eierFarge || "#8B5CF6" }}>{r.ansvarlig[0]}</i>{r.ansvarlig}</>
          : <><i className="ut">!</i>Unassigned</>}
      </button>
      {meny && <MedlemsMeny medlemmer={medl} onVelg={velg} onLukk={() => setMeny(false)} />}
    </span>
  );
}

function NesteEditor({ r, editable, onSatt }) {
  const [aapen, setAapen] = useState(false);
  const [tekst, setTekst] = useState(r.nesteSteg || "");
  const [due, setDue] = useState(r.frist || "");
  const kanRedigere = editable && r.type !== "task";

  const lagre = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setAapen(false);
    try {
      await patchNeste(r, tekst.trim(), due);
      onSatt();
    } catch { toast.error("Kunne ikke lagre next move"); }
  };

  if (aapen) {
    return (
      <form className="dok-neste-form" onClick={(e) => e.stopPropagation()} onSubmit={lagre} data-testid={`dok-neste-form-${r.id}`}>
        <input value={tekst} onChange={(e) => setTekst(e.target.value)} placeholder="Next move …" autoFocus data-testid={`dok-neste-input-${r.id}`} />
        <input type="date" value={due} onChange={(e) => setDue(e.target.value)} data-testid={`dok-neste-due-${r.id}`} />
        <button type="submit" data-testid={`dok-neste-lagre-${r.id}`}>Lagre</button>
      </form>
    );
  }
  const innhold = r.nesteSteg
    ? <span className="dok-rad-next" data-testid={`dok-rad-next-${r.id}`}>→ {r.nesteSteg}</span>
    : kanRedigere ? <span className="dok-rad-next mangler">+ next move</span> : null;
  if (!kanRedigere) return innhold;
  return (
    <button type="button" className="dok-neste-knapp" data-testid={`dok-neste-rediger-${r.id}`}
      onClick={(e) => { e.stopPropagation(); setTekst(r.nesteSteg || ""); setDue(r.frist || ""); setAapen(true); }}>
      {innhold}
    </button>
  );
}

function AnkerKort({ k, label, rader, valgt, onVelg, lead, editable, onLead }) {
  const [st, stLabel] = sporStatus(rader);
  const [meny, setMeny] = useState(false);
  const [medl, setMedl] = useState([]);
  const aktive = rader.filter(erAktiv).length;
  const aapneLead = async (e) => {
    e.stopPropagation();
    if (!editable) return;
    try { setMedl(await hentMedlemmer()); setMeny(true); } catch { toast.error("Kunne ikke hente teamet"); }
  };
  return (
    <button type="button" onClick={onVelg} className={`dok-anker-kort s-${st} ${valgt ? "valgt" : ""}`} data-testid={`dok-anker-${k}`}>
      <span className="dok-anker-topp">
        <span className={`dok-anker-status s-${st}`}>{stLabel}</span>
        <span className={`dok-anker-lead ${editable ? "kan" : ""}`} onClick={aapneLead} role="button" tabIndex={-1} data-testid={`dok-anker-lead-${k}`}>
          Lead: <b>{lead?.navn || (editable ? "velg" : "—")}</b>
        </span>
      </span>
      <p className="dok-anker-navn">{label}</p>
      <p className="dok-anker-tall">{rader.length} targets · {aktive} i dialog</p>
      <p className="dok-anker-kandidater">{rader.slice(0, 4).map((r) => r.navn.split(" — ")[0]).join(" · ") || "Ingen targets ennå"}</p>
      <span className="dok-anker-bunn">
        <span className="dok-anker-milepael">Next milestone: {MILEPAEL[k]}</span>
        <span className="dok-anker-antall"><ChevronRight size={10} /></span>
      </span>
      {meny && (
        <MedlemsMeny medlemmer={medl} onLukk={() => setMeny(false)}
          onVelg={(m) => { setMeny(false); onLead(m ? { id: m.id, navn: m.name } : null); }} />
      )}
    </button>
  );
}

function MyWorkKort({ eier, rader, naviger }) {
  const [alle, setAlle] = useState(false);
  const vis = alle ? rader : rader.slice(0, 3);
  return (
    <div className="dok-mywork-kort" data-testid={`dok-needs-${eier.id}`}>
      <p className="dok-needs-hode">
        {eier.id === "uten" ? <i className="ut">!</i> : <i style={{ background: eier.farge || "#8B5CF6" }}>{eier.navn[0]}</i>}
        {eier.navn}
        <b>{rader.length} open</b>
      </p>
      {vis.map((r) => (
        <button key={`${r.type}-${r.id}`} type="button" className="dok-needs-rad" onClick={() => naviger(r)}>
          <span className="tekst">
            {r.type !== "task" && r.nesteSteg ? <><i>{r.navn.split(" — ")[0]}</i> — {r.nesteSteg}</> : (r.nesteSteg || r.navn)}
          </span>
          <Due frist={r.frist} />
        </button>
      ))}
      {rader.length > 3 && (
        <button type="button" className="dok-vis-alle" onClick={() => setAlle(!alle)} data-testid={`dok-mywork-alle-${eier.id}`}>
          {alle ? "Vis færre" : `Se alle ${rader.length}`}
        </button>
      )}
    </div>
  );
}

export default function SprintVisning({ rader, naviger, spec, endre, editable, refresh }) {
  const [fokus, setFokus] = useState("now");
  const [spor, setSpor] = useState(null);
  const [eier, setEier] = useState(null);
  const [utvidet, setUtvidet] = useState({});
  const [tetthet, setTetthet] = useState(spec?.tetthet || "lesbar");
  const [gruppering, setGruppering] = useState(spec?.gruppering || "owner");

  const settGruppering = (k) => {
    setGruppering(k);
    if (editable) endre?.({ gruppering: k });
  };

  const targets = useMemo(() => rader.filter((r) => r.type !== "task"), [rader]);
  const tasks = useMemo(() => rader.filter((r) => r.type === "task"), [rader]);

  const status = useMemo(() => ({
    due: rader.filter((r) => r.frist).length,
    dialog: targets.filter(erAktiv).length,
    sikret: SPOR.filter(([k]) => targets.some((r) => r.spor === k && SIKRET.has(r.stage))).length,
    now: targets.filter((r) => r.fokus === "now").length,
  }), [rader, targets]);

  const eiere = useMemo(() => {
    const m = new Map();
    rader.forEach((r) => {
      const key = r.eierId || "uten";
      const e = m.get(key) || { id: key, navn: r.ansvarlig || "Unassigned", farge: r.eierFarge, antall: 0 };
      e.antall += 1;
      m.set(key, e);
    });
    return [...m.values()].sort((a, b) => (a.id === "uten") - (b.id === "uten") || b.antall - a.antall);
  }, [rader]);

  const antall = useMemo(() => ({
    now: targets.filter((r) => r.fokus === "now").length,
    next: targets.filter((r) => r.fokus === "next").length,
    alle: targets.length,
  }), [targets]);

  const matcherEier = (r) => !eier || (eier === "uten" ? !r.eierId : r.eierId === eier);
  const synlige = useMemo(() => targets.filter((r) =>
    (fokus === "alle" || r.fokus === fokus) && (!spor || r.spor === spor) && matcherEier(r)),
  [targets, fokus, spor, eier]); // eslint-disable-line react-hooks/exhaustive-deps

  const grupper = useMemo(() => {
    const g = SPOR_ALLE.map(([k, l]) => [k, l, synlige.filter((r) => r.spor === k)]).filter((x) => x[2].length);
    const uten = synlige.filter((r) => !SPOR_ALLE.some(([k]) => k === r.spor));
    if (uten.length) g.push(["uten", "Uten spor", uten]);
    return g;
  }, [synlige]);

  const mywork = useMemo(() => {
    const beveg = [...tasks, ...targets.filter((r) => r.nesteSteg && r.fokus === "now")];
    const m = new Map();
    beveg.forEach((r) => {
      const key = r.eierId || "uten";
      const e = m.get(key) || { id: key, navn: r.ansvarlig || "Unassigned", farge: r.eierFarge, rader: [] };
      e.rader.push(r);
      m.set(key, e);
    });
    m.forEach((e) => e.rader.sort((a, b) => (a.frist || "9999") < (b.frist || "9999") ? -1 : 1));
    return [...m.values()].sort((a, b) => (a.id === "uten") - (b.id === "uten"));
  }, [tasks, targets]);

  const leads = spec?.leads || {};
  const settLead = (k, lead) => endre?.({ leads: { ...leads, [k]: lead } });

  const settPri = (e, r) => {
    e.stopPropagation();
    if (!editable) return;
    const items = (spec?.items || []).map((it) =>
      it.id === r.id ? { ...it, pri: it.pri ? PRI_NESTE[it.pri] : "P1" } : it);
    endre?.({ items });
  };

  const leggTilTarget = async (eierId, valg) => {
    try {
      let kilde = valg.kilde;
      let id = valg.id;
      if (valg.nyttNavn) {
        const { data } = await api.post("/admin/os/speakers", {
          name: valg.nyttNavn, actor: "Dokument",
          ownerId: eierId && eierId !== "uten" ? eierId : null,
        });
        kilde = "speakers";
        id = data.item.id;
      } else if (eierId && eierId !== "uten") {
        await patchEier({ type: kilde === "partners" ? "partner" : "speaker", id }, eierId);
      }
      const items = spec?.items || [];
      if (items.some((i) => i.id === id)) {
        toast.info("Target var allerede i listen — eier oppdatert");
      } else {
        endre?.({ items: [...items, { kilde, id, maal: "", fokus: fokus === "alle" ? "now" : fokus, spor: "" }] });
        toast.success("Target lagt til");
      }
      refresh();
    } catch {
      toast.error("Kunne ikke legge til target");
    }
  };

  const Rad = ({ r }) => (
    <div className="dok-target-rad" role="button" tabIndex={0}
      onClick={() => naviger(r)} onKeyDown={(e) => e.key === "Enter" && naviger(r)}
      data-testid={`dok-blokk-rad-${r.id}`}>
      {(r.pri || editable) && (
        <button type="button" className={`dok-pri ${r.pri ? `p-${r.pri.toLowerCase()}` : "tom"}`}
          onClick={(e) => settPri(e, r)} disabled={!editable}
          title={editable ? "Klikk for å endre prioritet" : ""} data-testid={`dok-pri-${r.id}`}>
          {r.pri || "—"}
        </button>
      )}
      <span className="dok-target-venstre">
        <span className="dok-target-navn">
          {r.navn}
          {r.under && !r.navn.includes(r.under) && <span className="dok-target-under"> — {r.under}</span>}
        </span>
        {tetthet === "lesbar" && r.maal && <span className="dok-target-maal">{r.maal}</span>}
        <NesteEditor r={r} editable={editable} onSatt={refresh} />
      </span>
      <span className="dok-target-side">
        <EierChip r={r} editable={editable} onSatt={refresh} />
        <Due frist={r.frist} />
        <StatusChip r={r} />
      </span>
      <ChevronRight size={12} className="dok-target-pil" />
    </div>
  );

  return (
    <div contentEditable={false}>
      <div className="dok-hero-status" data-testid="dok-hero-status">
        {[[status.due, "actions due"], [status.dialog, "active dialogues"], [`${status.sikret}/3`, "anchors secured"], [status.now, "NOW targets"]].map(([v, l]) => (
          <span key={l} className="dok-hero-tall"><b>{v}</b> {l}</span>
        ))}
      </div>

      {mywork.length > 0 && (
        <div className="dok-mywork" data-testid="dok-needs-action">
          <p className="dok-sprint-gruppelabel">This week — who needs to move</p>
          <div className="dok-needs-grid">
            {mywork.map((e) => <MyWorkKort key={e.id} eier={e} rader={e.rader} naviger={naviger} />)}
          </div>
        </div>
      )}

      <div className="dok-anker-grid" data-testid="dok-anker-grid">
        {SPOR.map(([k, l]) => (
          <AnkerKort key={k} k={k} label={l} rader={targets.filter((r) => r.spor === k)}
            valgt={spor === k} onVelg={() => { setSpor(spor === k ? null : k); setFokus("alle"); }}
            lead={leads[k]} editable={editable} onLead={(m) => settLead(k, m)} />
        ))}
      </div>
      <p className="dok-anker-caption">3 ankere → public launch</p>

      <div className="dok-kontrollbar" data-testid="dok-eier-strip">
        <span className="dok-seg2" data-testid="dok-sprint-visning">
          {[["owner", "By owner"], ["track", "By track"]].map(([k, l]) => (
            <button key={k} type="button" onClick={() => settGruppering(k)}
              className={gruppering === k ? "aktiv" : ""} data-testid={`dok-visning-${k}`}>{l}</button>
          ))}
        </span>
        <span className="dok-seg2" data-testid="dok-sprint-toggle">
          {[["now", `Now ${antall.now}`], ["next", `Next ${antall.next}`], ["alle", `All ${antall.alle}`]].map(([k, l]) => (
            <button key={k} type="button" onClick={() => setFokus(k)}
              className={fokus === k ? "aktiv" : ""} data-testid={`dok-sprint-${k}`}>{l}</button>
          ))}
        </span>
        {spor && (
          <button type="button" onClick={() => setSpor(null)} className="dok-strip-chip aktiv" data-testid="dok-sprint-fjern-spor">
            {SPOR_ALLE.find(([k]) => k === spor)?.[1]} <X size={10} />
          </button>
        )}
        <span className="dok-eierfilter">
          {eiere.map((e) => (
            <button key={e.id} type="button"
              onClick={() => setEier(eier === e.id ? null : e.id)}
              className={`dok-ftab ${e.id === "uten" ? "uten" : ""} ${eier === e.id ? "aktiv" : ""}`}
              data-testid={`dok-eier-filter-${e.id}`}>
              {e.id === "uten" ? <i className="ut">!</i> : <i style={{ background: e.farge || "#8B5CF6" }}>{e.navn[0]}</i>}
              {e.navn} <b>{e.antall}</b>
            </button>
          ))}
          {eier && (
            <button type="button" className="dok-strip-nullstill" onClick={() => setEier(null)} data-testid="dok-eier-filter-fjern">
              Vis alle <X size={10} />
            </button>
          )}
          {gruppering === "track" && (
            <button type="button" className={`dok-ftab ${tetthet === "kompakt" ? "aktiv" : ""}`}
              onClick={() => setTetthet(tetthet === "kompakt" ? "lesbar" : "kompakt")} data-testid="dok-tetthet-toggle">
              Compact
            </button>
          )}
        </span>
      </div>

      {gruppering === "owner" ? (
        <AssignmentTabell rader={synlige} naviger={naviger} editable={editable} onNyTarget={leggTilTarget} />
      ) : (<>
      {grupper.length === 0 && <p className="dok-blokk-tom">Ingen targets i dette utvalget.</p>}
      <div className={tetthet === "kompakt" ? "dok-sprint-kompakt" : ""}>
      {grupper.map(([k, label, rs]) => {
        const vis = utvidet[k] ? rs : rs.slice(0, 5);
        const lead = leads[k]?.navn;
        return (
          <div key={k} className="dok-sprint-gruppe">
            <div className="dok-seksjon-hode">
              <p className="t">{label}</p>
              <p className="u">
                {rs.length} targets{lead ? <> · Lead: <b>{lead}</b></> : ""}{SPOR_GOAL[k] ? <> · Goal: {SPOR_GOAL[k]}</> : ""}
              </p>
            </div>
            {vis.map((r) => <Rad key={`${r.type}-${r.id}`} r={r} />)}
            {rs.length > 5 && (
              <button type="button" className="dok-vis-alle" onClick={() => setUtvidet({ ...utvidet, [k]: !utvidet[k] })}
                data-testid={`dok-gruppe-alle-${k}`}>
                {utvidet[k] ? "Vis færre" : `Vis alle ${rs.length}`}
              </button>
            )}
          </div>
        );
      })}
      </div>
      </>)}
    </div>
  );
}
